// frontend/src/components/Reports/AttendanceReports.js
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation, Link } from 'react-router-dom';

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const AttendanceReports = () => {
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => {
    const dt = new Date();
    dt.setDate(dt.getDate() - 7);
    return dt;
  }, []);

  const [startDate, setStartDate] = useState(formatDate(defaultStart));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [reportType, setReportType] = useState('records'); // 'records' | 'daily_summary' | 'group_summary' | 'student_summary'
  const [teacherId, setTeacherId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [gradeLevelId, setGradeLevelId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const location = useLocation();

  // Fetch reports (memoized)
  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        start_date: startDate,
        end_date: endDate,
        type: reportType,
      };
      if (teacherId) params.teacher_id = teacherId;
      if (studentId) params.student_id = studentId;
      if (gradeLevelId) params.grade_level_id = gradeLevelId;
      const res = await axios.get('/attendance/reports/', { params });
      setData(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load report.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, reportType, teacherId, studentId, gradeLevelId]);

  // Fetch using explicit params (used for URL-driven auto-run)
  const fetchReportsWithParams = useCallback(async (p) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/attendance/reports/', { params: p });
      setData(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load report.');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Parse URL params to prefill filters (e.g., from Student List) and auto-run if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sd = params.get('start_date');
    const ed = params.get('end_date');
    const rt = params.get('type');
    const tid = params.get('teacher_id');
    const sid = params.get('student_id');
    const gl = params.get('grade_level_id');
    if (sd) setStartDate(sd);
    if (ed) setEndDate(ed);
    if (rt) setReportType(rt);
    if (tid) setTeacherId(tid);
    if (sid) setStudentId(sid);
    if (gl) setGradeLevelId(gl);
    const hasAny = sd || ed || rt || tid || sid || gl;
    if (hasAny) {
      const p = {
        start_date: sd || startDate,
        end_date: ed || endDate,
        type: rt || reportType,
      };
      if (tid) p.teacher_id = tid;
      if (sid) p.student_id = sid;
      if (gl) p.grade_level_id = gl;
      fetchReportsWithParams(p);
    }
  }, [location.search]);

  // Load teachers and grade levels for dropdowns
  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, gRes] = await Promise.all([
          axios.get('/api/teachers/all/'),
          axios.get('/courses/api/grade_levels/'),
        ]);
        setTeachers(Array.isArray(tRes.data) ? tRes.data : (tRes.data?.results || []));
        setGradeLevels(Array.isArray(gRes.data) ? gRes.data : (gRes.data?.results || []));
      } catch (e) {
        // non-fatal
      }
    };
    load();
  }, []);


  const downloadCSV = () => {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      type: reportType,
      csv: '1',
    });
    if (teacherId) params.append('teacher_id', teacherId);
    if (studentId) params.append('student_id', studentId);
    if (gradeLevelId) params.append('grade_level_id', gradeLevelId);
    // Use full axios base or relative (React dev proxy not configured; axios baseURL points to backend)
    const url = `${axios.defaults.baseURL}/attendance/reports/?${params.toString()}`;
    window.location.href = url;
  };

  const renderTable = () => {
    if (reportType === 'daily_summary') {
      if (!Array.isArray(data) || data.length === 0) return null;
      return (
        <table className="report-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Status</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{row.date}</td>
                <td>{row.status}</td>
                <td>{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    // records
    if (!Array.isArray(data) || data.length === 0) return null;
    return (
      <table className="report-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Group</th>
            <th>Student</th>
            <th>Teacher</th>
            <th>Status</th>
            <th>Excuse</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td>{row.date}</td>
              <td>{row.group}</td>
              <td>{row.student}</td>
              <td>{row.teacher}</td>
              <td>{row.status}</td>
              <td>{row.excuse || ''}</td>
              <td>{row.comment || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderGroupSummary = () => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return (
      <table className="report-table">
        <thead>
          <tr>
            <th>Group</th>
            <th>Present (P)</th>
            <th>Absent (A)</th>
            <th>Tardy (T)</th>
            <th>Excused (E)</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td>{row.group}</td>
              <td>{row.P || 0}</td>
              <td>{row.A || 0}</td>
              <td>{row.T || 0}</td>
              <td>{row.E || 0}</td>
              <td>{row.total || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderStudentSummary = () => {
    if (!Array.isArray(data) || data.length === 0) return null;
    return (
      <table className="report-table">
        <thead>
          <tr>
            <th>Student ID</th>
            <th>Student</th>
            <th>Present (P)</th>
            <th>Absent (A)</th>
            <th>Tardy (T)</th>
            <th>Excused (E)</th>
            <th>Total</th>
            <th>Attendance Rate %</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={idx}>
              <td>{row.student_id || ''}</td>
              <td>
                {row.student_id ? (
                  <Link
                    to={`/reports?start_date=${startDate}&end_date=${endDate}&type=records&student_id=${row.student_id}`}
                    title="View this student's attendance records"
                  >
                    {row.student}
                  </Link>
                ) : (
                  row.student
                )}
              </td>
              <td>{row.P || 0}</td>
              <td>{row.A || 0}</td>
              <td>{row.T || 0}</td>
              <td>{row.E || 0}</td>
              <td>{row.total || 0}</td>
              <td>{row.attendance_rate?.toFixed ? row.attendance_rate.toFixed(2) : row.attendance_rate}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="reports-container">
      <h1>Attendance Reports</h1>
      <p>Filter and export attendance data.</p>

      <div className="filters">
        <div className="filter-row">
          <label>
            Start Date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label>
            End Date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
          <label>
            Type
            <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              <option value="records">Records</option>
              <option value="daily_summary">Daily Summary</option>
              <option value="group_summary">Group Summary</option>
              <option value="student_summary">Student Summary</option>
            </select>
          </label>
        </div>
        <div className="filter-row">
          <label>
            Teacher ID (optional)
            <select value={teacherId} onChange={(e) => setTeacherId(e.target.value)}>
              <option value="">All</option>
              {teachers.map(t => (
                <option key={t.id} value={t.id}>{t.first_name ? `${t.first_name} ${t.last_name}` : t.name || `Teacher ${t.id}`}</option>
              ))}
            </select>
          </label>
          <label>
            Student ID (optional)
            <input type="text" value={studentId} onChange={(e) => setStudentId(e.target.value)} placeholder="e.g. 42" />
          </label>
          <label>
            Grade Level (optional)
            <select value={gradeLevelId} onChange={(e) => setGradeLevelId(e.target.value)}>
              <option value="">All</option>
              {gradeLevels.map(gl => (
                <option key={gl.id} value={gl.id}>{gl.name || gl.label || `Grade ${gl.grade}`}</option>
              ))}
            </select>
          </label>
          <div className="actions">
            <button onClick={fetchReports} disabled={loading}>
              {loading ? 'Loading…' : 'Run Report'}
            </button>
            <button onClick={downloadCSV} disabled={loading}>
              Download CSV
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error">{error}</div>}

      <div className="results">
        {loading ? <div>Loading…</div> : (
          reportType === 'records' ? renderTable() :
          reportType === 'daily_summary' ? renderTable() :
          reportType === 'group_summary' ? renderGroupSummary() :
          reportType === 'student_summary' ? renderStudentSummary() : null
        )}
        {!loading && Array.isArray(data) && data.length === 0 && (
          <div>No data yet. Run a report.</div>
        )}
      </div>

      <style>{`
        .reports-container { max-width: 1100px; margin: 30px auto; padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
        .filters { margin-bottom: 16px; }
        .filter-row { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; align-items: flex-end; }
        label { display: flex; flex-direction: column; font-size: 0.9rem; color: #333; }
        input, select { padding: 8px 10px; border: 1px solid #ccc; border-radius: 6px; min-width: 180px; }
        .actions { display: flex; gap: 10px; }
        button { padding: 10px 14px; background: #1976d2; color: #fff; border: none; border-radius: 6px; cursor: pointer; }
        button[disabled] { opacity: 0.6; cursor: not-allowed; }
        .error { color: #b00020; margin: 10px 0; }
        .report-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
        .report-table th, .report-table td { border: 1px solid #eee; padding: 8px; text-align: left; font-size: 0.95rem; }
        .report-table th { background: #f5f5f5; }
      `}</style>
    </div>
  );
};

export default AttendanceReports;
